/**
 * Test Mode Helper
 * 
 * Utility functions to easily enable/disable test mode for Shopify integration testing
 */

import { checkoutService } from '../services/checkoutService';
import { debugLog } from './debugLog';

export const enableTestMode = () => {
  checkoutService.enableTestMode();
  localStorage.setItem('shopify-test-mode', 'true');
  debugLog('🧪 Test mode enabled - only test products will be shown');
  
  // Reload the page to apply changes
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

export const disableTestMode = () => {
  checkoutService.disableTestMode();
  localStorage.removeItem('shopify-test-mode');
  debugLog('🏪 Test mode disabled - all products will be shown');
  
  // Reload the page to apply changes
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

export const isTestModeEnabled = () => {
  return checkoutService.isTestMode();
};

export const getTestModeStatus = () => {
  const enabled = isTestModeEnabled();
  debugLog(`Test mode is ${enabled ? 'ENABLED' : 'DISABLED'}`);
  return enabled;
};

export const debugTestModeCartState = () => {
  const localCart = localStorage.getItem('eruption-radio-cart');
  const shopifyCartId = localStorage.getItem('shopify-cart-id');
  
  debugLog('🛒 Cart Debug Info:');
  debugLog('Local cart:', localCart ? JSON.parse(localCart) : 'Empty');
  debugLog('Shopify cart ID:', shopifyCartId);
  
  return {
    localCart: localCart ? JSON.parse(localCart) : null,
    shopifyCartId
  };
};

export const debugProductData = () => {
  // This will help debug what products are being loaded
  debugLog('🔍 Checking for products in the DOM...');
  
  // Look for product cards
  const productCards = document.querySelectorAll('[data-product-id]');
  debugLog(`Found ${productCards.length} product cards`);
  
  productCards.forEach((card, index) => {
    const productId = card.getAttribute('data-product-id');
    const title = card.querySelector('h3')?.textContent;
    const price = card.querySelector('[data-price]')?.textContent;
    const addToCartBtn = card.querySelector('button[data-add-to-cart]');
    
    debugLog(`Product ${index + 1}:`, {
      id: productId,
      title,
      price,
      addToCartDisabled: addToCartBtn?.hasAttribute('disabled')
    });
  });
};

export const clearAllCartData = () => {
  localStorage.removeItem('eruption-radio-cart');
  localStorage.removeItem('shopify-cart-id');
  debugLog('🧹 Cleared all cart data');
  
  // Dispatch cart updated event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cart-updated'));
  }
};

// Global functions for browser console
declare global {
  interface Window {
    enableTestMode?: typeof enableTestMode;
    disableTestMode?: typeof disableTestMode;
    getTestModeStatus?: typeof getTestModeStatus;
    debugTestModeCartState?: typeof debugTestModeCartState;
    debugProductData?: typeof debugProductData;
    clearAllCartData?: typeof clearAllCartData;
    forceRefreshLiveShow?: () => Promise<unknown>;
  }
}

if (typeof window !== 'undefined') {
  window.enableTestMode = enableTestMode;
  window.disableTestMode = disableTestMode;
  window.getTestModeStatus = getTestModeStatus;
  window.debugTestModeCartState = debugTestModeCartState;
  window.debugProductData = debugProductData;
  window.clearAllCartData = clearAllCartData;
  
  // Live show debugging
  window.forceRefreshLiveShow = async () => {
    debugLog('🔄 Manually forcing live show refresh...');
    const { cachingStrategyService } = await import('../services/cachingStrategyService');
    return cachingStrategyService.forceRefreshLiveShow();
  };
}
